import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MetaDataService } from '../../../services/meta-data.service';
import { CalendarModule } from 'primeng/calendar';
import { CommonBindingDataService } from '../../../services/common-binding-data.service';
import { AppSettings } from 'app/modules/shared/app.settings';
import { forkJoin, Subscription, combineLatest } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-date-input',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CalendarModule],
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.scss'
})
export class DateInputComponent implements OnInit, OnDestroy {
  @Input() field: any;
  formName: FormGroup;
  @Input() attributeLabels: [];
  @Input() moduleName: string;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() dateFormat = this.metaDataService.lowerDateFormat;
  @Input() isFilter: boolean = false;
  placeholder: string;
  minDateOfEndDate;
  dateModules = ['audit_logs_grid', 'trial_reports_grid', 'order_reports_grid'];
  today: Date = new Date();
  minEndDate: Date = new Date();
  disabledDates: Date[] = [];
  private subscriptions: Subscription[] = [];
  actionStatus: string | null = null;

  minDateForSelectedField: Date;
  fieldForMinDate: string[] = [];
  constructor(private formGroupDirective: FormGroupDirective, private metaDataService: MetaDataService,
    public cs: CommonBindingDataService, private messageService: MessageService, private route: ActivatedRoute,
    private router: Router) {
    this.formName = this.formGroupDirective.control;
  }

  ngOnInit(): void {
    this.cs.actionStatus$.subscribe(status => {
      this.actionStatus = status;
    });
    if (this.field.validation && this.field.validation.max && this.field.validation.min) {
      this.field.validation.max = new Date(this.field.validation.max);
      this.field.validation.min = new Date(this.field.validation.min);
    }
    if (this.dateFormat) {
      return;
    } else {
      this.dateFormat = AppSettings.CALENDAR_DD_MM_YY;
    }
    if (this.field.attributeCode === 'commencement_date') {
      const control = this.formName.controls.commencement_date;

      if (control.value !== null && control.value !== undefined) {
        this.cs.emitDateValue(control.value);
      }

      control.valueChanges.subscribe(change => {
        this.cs.emitDateValue(change);
      });
    }

    this.today.setHours(0, 0, 0, 0);

    const isAddMode = this.isAddMode();
    const controlName = this.field?.isOneToMultiple ? this.field?.attributeCodeOneToMultiple : this.field.attributeCode;
    const control = this.formName.get(controlName);
    const selectedDate: Date | null = null;

    if (this.moduleName === 'protocol' && this.field.attributeCode === 'start_date') {
      if (isAddMode && control && !control.value) {
        control.setValue(this.today);
      }

      const isRunningProtocolStartDate =
        this.moduleName === 'protocol' &&
        this.field.attributeCode === 'start_date' &&
        this.actionStatus === 'Running';

      if (isRunningProtocolStartDate && control?.value) {
        const selectedDate = new Date(control.value);
        selectedDate.setHours(0, 0, 0, 0);
        if (selectedDate < this.today) {
          control.disable();
        }
      }
    }

    this.disabledDates = [];

    const earliestDate = new Date(2000, 0, 1);
    for (let d = new Date(earliestDate); d < this.today; d.setDate(d.getDate() + 1)) {
      if (!selectedDate || d.getTime() !== selectedDate.getTime()) {
        this.disabledDates.push(new Date(d));
      }
    }

    if (this.field.attributeCode === 'next_visit') {
      this.formName.controls.next_visit.disable();

      const visitFrequencyControl = this.formName.controls.visit_frequency;
      if (visitFrequencyControl) {
        if (visitFrequencyControl.value !== null && visitFrequencyControl.value !== undefined) {
          this.cs.emitDaysValue(visitFrequencyControl.value);
        }

        visitFrequencyControl.valueChanges.subscribe(change => {
          this.cs.emitDaysValue(change);
        });
      }

      const nextVisitSubscription = combineLatest([
        this.cs.commenceDate$,
        this.cs.visitFrequencyDays$
      ]).subscribe(([date, days]) => {
        if (date && days > 0) {
          this.calculateNextVisitWithPauseDates(date, days);
        } else if (date && (!days || days <= 0)) {
          // If we have a commencement date but no valid frequency, clear the next visit
          this.formName.controls.next_visit.setValue(null);
          this.formName.controls.next_visit.markAsTouched();
          this.formName.controls.next_visit.updateValueAndValidity();
        }
      });

      if (nextVisitSubscription) {
        this.subscriptions.push(nextVisitSubscription);
      }

      // Add subscriptions to pause date changes to validate next visit
      const pauseFromControl = this.formName.controls.pause_from_date;
      const pauseToControl = this.formName.controls.pause_to_date;

      if (pauseFromControl) {
        const pauseFromSubscription = pauseFromControl.valueChanges.subscribe(() => {
          this.validateAndAdjustNextVisitDate();
        });
        this.subscriptions.push(pauseFromSubscription);
      }

      if (pauseToControl) {
        const pauseToSubscription = pauseToControl.valueChanges.subscribe(() => {
          this.validateAndAdjustNextVisitDate();
        });
        this.subscriptions.push(pauseToSubscription);
      }
    }
    if (this.isFilter) {
      this.placeholder = this.cs.getLabelValue(`${this.moduleName}.fields.${this.field.attributeCode}.label`, this.attributeLabels, null);
    } else if ((this.moduleName === 'audit_logs_grid' || this.moduleName === 'trial_reports_grid') && this.field.attributeCode === 'fromDate') {
      this.placeholder = this.cs.getLabel('audit_logs.lbl_startDate')
    } else if (this.dateModules.includes(this.moduleName) && this.field.attributeCode === 'toDate') {
      this.placeholder = this.cs.getLabel('audit_logs.lbl_end_date')
    } else if (this.moduleName === 'protocol' && this.field.attributeCode === 'start_date') {
      this.placeholder = this.cs.getLabel('lbl_start_date')
    } else if (this.moduleName === 'protocol' && this.field.attributeCode === 'end_date') {
      this.placeholder = this.cs.getLabel('lbl_end_date')
    } else {
      this.placeholder = this.cs.getLabelValue(`${this.moduleName}.fields.${this.field.attributeCode}.label`, this.attributeLabels, 'Select Date');
    }
    if (this.moduleName === AppSettings.ENTITY_CODE.PASSENGER || this.moduleName === AppSettings.ENTITY_CODE.PASS_PRIVILEGE_CONF) {
      this.onChangeForPrivilegeSettings();
    }

    const initialStartDate = this.formName.controls.start_date?.value;
    if (initialStartDate) {
      this.minEndDate = new Date(initialStartDate);
    } else {
      this.minEndDate = new Date();
    }

    const startDateSubscription = this.formName.controls.start_date?.valueChanges.subscribe(value => {
      if (value) {
        this.today = new Date(value);
        this.minEndDate = new Date(value);
      }
    });
    if (startDateSubscription) {
      this.subscriptions.push(startDateSubscription);
    }

    const subscription = this.formName.controls.end_date?.valueChanges.subscribe(value => {
      if (value) {
        const endDate = new Date(value);
        if (this.minEndDate && endDate < this.minEndDate) {
          this.formName.controls.end_date.setValue(null);
        }
      }
    });
    if (subscription) {
      this.subscriptions.push(subscription);
    }
  }

  isAddMode(): boolean {
    return this.router.url.includes('add-trial');
  }

  getObservableValues() {
    return forkJoin({
      commenceDate: this.cs.commenceDate$.subscribe(date => date),
      frequencyDays: this.cs.visitFrequencyDays$.subscribe(days => days)
    })
  }

  onChangeForPrivilegeSettings() {
    this.formName.controls[AppSettings.DATE_ATTRIBUTE_IDS.APP_ACCESS_START_DATE].valueChanges.subscribe(value => {
      if (value) {
        const startDate = new Date(value);
        this.minDateOfEndDate = startDate;
      }
    });
    this.formName.controls[AppSettings.DATE_ATTRIBUTE_IDS.TRANSPORTATION_START_DATE].valueChanges.subscribe(value => {
      if (value) {
        const startDate = new Date(value);
        this.minDateOfEndDate = startDate;
      }
    });
  }

  private skipPauseDates(date: Date): Date {
    // First check form controls for pause dates
    const pauseFromControl = this.formName.controls.pause_from_date;
    const pauseToControl = this.formName.controls.pause_to_date;

    let pauseFromDate: Date | null = null;
    let pauseToDate: Date | null = null;

    // Get pause dates from form controls if available
    if (pauseFromControl?.value && pauseToControl?.value) {
      pauseFromDate = new Date(pauseFromControl.value);
      pauseToDate = new Date(pauseToControl.value);
    } else {
      // If not in form, get from service
      const pauseDates = this.cs.getPauseDates();
      pauseFromDate = pauseDates.pauseFromDate;
      pauseToDate = pauseDates.pauseToDate;
    }

    // Ensure we have valid Date objects (handle timestamps)
    if (pauseFromDate && typeof pauseFromDate === 'number') {
      pauseFromDate = new Date(pauseFromDate);
    }
    if (pauseToDate && typeof pauseToDate === 'number') {
      pauseToDate = new Date(pauseToDate);
    }

    if (pauseFromDate && pauseToDate) {
      // Ensure all dates are normalized to start of day for consistent comparison
      pauseFromDate.setHours(0, 0, 0, 0);
      pauseToDate.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      // Check if the date falls within the pause period
      if (date >= pauseFromDate && date <= pauseToDate) {
        // Move the date to the day after the pause period ends
        const newDate = new Date(pauseToDate);
        newDate.setDate(newDate.getDate() + 1);
        newDate.setHours(0, 0, 0, 0); // Ensure the new date is also normalized
        // Recursively check if the new date also falls within pause period
        return this.skipPauseDates(newDate);
      }
    }

    return date;
  }

  private hasPauseDates(): boolean {
    const pauseFromControl = this.formName.controls.pause_from_date;
    const pauseToControl = this.formName.controls.pause_to_date;

    // Check form controls first
    if (pauseFromControl?.value && pauseToControl?.value) {
      return true;
    }

    // If not in form, check service
    return this.cs.hasPauseDates();
  }

  private isDateInPausePeriod(date: Date): boolean {
    // First check form controls for pause dates
    const pauseFromControl = this.formName.controls.pause_from_date;
    const pauseToControl = this.formName.controls.pause_to_date;

    let pauseFromDate: Date | null = null;
    let pauseToDate: Date | null = null;

    // Get pause dates from form controls if available
    if (pauseFromControl?.value && pauseToControl?.value) {
      pauseFromDate = new Date(pauseFromControl.value);
      pauseToDate = new Date(pauseToControl.value);
    } else {
      // If not in form, get from service
      const pauseDates = this.cs.getPauseDates();
      pauseFromDate = pauseDates.pauseFromDate;
      pauseToDate = pauseDates.pauseToDate;
    }

    // Ensure we have valid Date objects (handle timestamps)
    if (pauseFromDate && typeof pauseFromDate === 'number') {
      pauseFromDate = new Date(pauseFromDate);
    }
    if (pauseToDate && typeof pauseToDate === 'number') {
      pauseToDate = new Date(pauseToDate);
    }

    if (pauseFromDate && pauseToDate) {
      pauseFromDate.setHours(0, 0, 0, 0);
      pauseToDate.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      return date >= pauseFromDate && date <= pauseToDate;
    }

    return false;
  }

  private calculateNextVisitWithPauseDates(date: Date, days: number) {

    const commencementDate = new Date(date);
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    commencementDate.setHours(0, 0, 0, 0);

    let nextVisitDate = new Date(commencementDate);

    if (commencementDate <= today) {
      const daysSinceCommencement = Math.floor((today.getTime() - commencementDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysSinceCommencement === 0) {
        nextVisitDate = new Date(commencementDate);
        nextVisitDate.setDate(commencementDate.getDate() + days);
      } else {
        const currentVisitDate = new Date(commencementDate);

        while (currentVisitDate < today) {
          currentVisitDate.setDate(currentVisitDate.getDate() + days);
        }

        if (currentVisitDate.getTime() === today.getTime()) {
          nextVisitDate = new Date(today);
        } else {
          nextVisitDate = currentVisitDate;
        }
      }
    } else {
      nextVisitDate.setDate(nextVisitDate.getDate() + days);
    }

    nextVisitDate = this.calculateNextVisitWithPauseDatesStepByStep(commencementDate, days, nextVisitDate);

    nextVisitDate.setHours(0, 0, 0, 0);


    this.formName.controls.next_visit.setValue(nextVisitDate);
    this.formName.controls.next_visit.markAsTouched();
    this.formName.controls.next_visit.updateValueAndValidity();
  }

  private calculateNextVisitWithPauseDatesStepByStep(commencementDate: Date, days: number, initialNextVisit: Date): Date {

    // Get pause dates
    const pauseDates = this.getPauseDates();
    const pauseFromDate = pauseDates.pauseFromDate;
    const pauseToDate = pauseDates.pauseToDate;

    if (!pauseFromDate || !pauseToDate) {
      return initialNextVisit;
    }

    // Normalize pause dates
    const normalizedPauseFrom = new Date(pauseFromDate);
    const normalizedPauseTo = new Date(pauseToDate);
    normalizedPauseFrom.setHours(0, 0, 0, 0);
    normalizedPauseTo.setHours(0, 0, 0, 0);


    // Check if the initial next visit falls within pause period
    if (initialNextVisit >= normalizedPauseFrom && initialNextVisit <= normalizedPauseTo) {

      // Move to the day after pause period ends
      const nextVisitAfterPause = new Date(normalizedPauseTo);
      nextVisitAfterPause.setDate(nextVisitAfterPause.getDate() + 1);
      nextVisitAfterPause.setHours(0, 0, 0, 0);

      return nextVisitAfterPause;
    }

    return initialNextVisit;
  }

  private getPauseDates(): { pauseFromDate: Date | null, pauseToDate: Date | null } {
    // First check form controls for pause dates
    const pauseFromControl = this.formName.controls.pause_from_date;
    const pauseToControl = this.formName.controls.pause_to_date;

    let pauseFromDate: Date | null = null;
    let pauseToDate: Date | null = null;

    // Get pause dates from form controls if available
    if (pauseFromControl?.value && pauseToControl?.value) {
      pauseFromDate = new Date(pauseFromControl.value);
      pauseToDate = new Date(pauseToControl.value);
    } else {
      // If not in form, get from service
      const pauseDates = this.cs.getPauseDates();
      pauseFromDate = pauseDates.pauseFromDate;
      pauseToDate = pauseDates.pauseToDate;
    }

    // Ensure we have valid Date objects (handle timestamps)
    if (pauseFromDate && typeof pauseFromDate === 'number') {
      pauseFromDate = new Date(pauseFromDate);
    }
    if (pauseToDate && typeof pauseToDate === 'number') {
      pauseToDate = new Date(pauseToDate);
    }

    return { pauseFromDate, pauseToDate };
  }

  private validateAndAdjustNextVisitDate() {
    const nextVisitControl = this.formName.controls.next_visit;
    if (nextVisitControl?.value) {
      const nextVisitDate = new Date(nextVisitControl.value);

      // Check if the current next visit date falls within pause period
      if (this.isDateInPausePeriod(nextVisitDate)) {
        // Adjust the date to be outside the pause period
        const adjustedDate = this.skipPauseDates(nextVisitDate);
        nextVisitControl.setValue(adjustedDate);
        nextVisitControl.markAsTouched();
        nextVisitControl.updateValueAndValidity();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    });
    this.subscriptions = [];
  }
}
